import { html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { deleteCookieValue, getCookieValue } from "../../utils/cookies";
import { cssReset } from "../../shared/css-reset";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { AsyncController } from "../../shared/controllers/async-controller";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { enforceConfig } from "../../shared/enforce-config";
import { LANGUAGES } from "../../api-sdk/types";
import { DEFAULT_BASE_URL } from "../../api-sdk/constants";
import { postTransaction, postChainEligibleNonprofitsForCart } from "../../api-sdk/v3/routes";
import "../redeem-transaction";
import "../impact-overview";
import { BEAM_CART_COOKIE_NAME, TBeamCartLocalStorage, TCart } from "../../shared/cart-contents";
import { BeamNonprofitSelectEvent, BeamOrderCreatedEvent } from "../../utils/events";

const REQUIRED_DATA_PROPS = ["baseUrl", "apiKey", "orderId", "email", "cartTotal", "currencyCode", "lang"];
const OPTIONAL_DATA_PROPS = ["storeId", "postalCode", "countryCode", "cart", "discountCodes"];

export class BeamPostPurchase extends LitElement {
  static tagName = "beam-post-purchase";

  @property({ type: String })
  public baseUrl: string = DEFAULT_BASE_URL;

  @property({ type: String })
  public apiKey!: string;

  @property({ type: Number, reflect: true })
  public storeId?: number; // Either storeId or postalCode, countryCode must be provided

  @property({ type: String })
  public postalCode?: string;

  @property({ type: String })
  public countryCode?: string;

  @property({ type: String })
  public orderId!: string;

  @property({ type: String })
  public email!: string;

  @property({ type: Number })
  public cartTotal!: number;

  @property({ type: String })
  public currencyCode = "USD";

  @property({ type: String })
  public lang: LANGUAGES = "en";

  @property({ type: Object })
  public cart?: TCart;

  @property({ type: Array })
  public discountCodes?: string[];

  @property({ type: String })
  public domain?: string;

  @property({ type: Boolean })
  public debug = false;

  @state() private activeWidget: "redeem-transaction" | "impact-overview" | null = null; // wait for postTransaction
  @state() private nonprofitId: number | null = null; // from localStorage or redeem-transaction result
  @state() private selectionId: string | null = null; // from localStorage
  @state() private cartId: string | null = null; // from localStorage, external cart ID
  @state() private beamCartId: string | null = null; // from cookie, Beam cart ID
  @state() private userId: string | null = null; // from postTransaction
  @state() private transactionId: string | null = null; // from postTransaction
  @state() private chainId: number | null = null; // from postTransaction, used by impact-overview

  private localStorage = createScopedLocalStorage(this);

  firstUpdated(_changedProperties: PropertyValues) {
    enforceConfig(REQUIRED_DATA_PROPS, this);
    this.nonprofitId = Number(this.localStorage.getItem("nonprofit")) || null;
    this.selectionId = this.localStorage.getItem("transaction") || null;
    this.cartId = this.localStorage.getItemJson<TBeamCartLocalStorage>("cart")?.cartId || null;
    // Reading beamCartId from cookies instead of localStorage allows it to work across subdomains
    this.beamCartId = getCookieValue(BEAM_CART_COOKIE_NAME) || null;

    if (this.selectionId && this.nonprofitId) {
      this.activeWidget = "impact-overview";
    }
  }

  async updated(previousPropertyValues: PropertyValues) {
    // Also fires on first load as props go from undefined => value
    const refreshOnChangeProps = [...REQUIRED_DATA_PROPS, ...OPTIONAL_DATA_PROPS];
    if (refreshOnChangeProps.some((p) => previousPropertyValues.has(p))) {
      await this.transactionDataController.exec();
    }
  }

  handlePostTransaction = async () => {
    enforceConfig(REQUIRED_DATA_PROPS, this);

    // Find storeId from postalCode + countryCode if it is not set
    if (!this.storeId && this.postalCode && this.countryCode) {
      const res = await postChainEligibleNonprofitsForCart({
        baseUrl: this.baseUrl,
        headers: {
          authorization: `Api-Key ${this.apiKey}`,
        },
        requestBody: {
          postalCode: this.postalCode,
          countryCode: this.countryCode,
          widgetName: "redeem-transaction",
          version: "1.0.0",
        },
      });
      this.storeId = res.store?.id;
    }

    const result = await postTransaction({
      baseUrl: this.baseUrl,
      headers: {
        authorization: `Api-Key ${this.apiKey}`,
      },
      requestBody: {
        nonprofitId: this.nonprofitId ?? undefined,
        selectionId: this.selectionId ?? undefined,
        cartId: this.cartId ?? undefined,
        beamCartId: this.beamCartId ?? undefined,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        storeId: this.storeId!,
        orderId: this.orderId,
        cartTotal: this.cartTotal,
        email: this.email,
        currencyCode: this.currencyCode,
        postalCode: this.postalCode,
        countryCode: this.countryCode,
        cart: this.cart,
        discountCodes: this.discountCodes,
      },
    });

    this.transactionId = result.transactionId;
    this.chainId = result.chainId;
    this.userId = result.beamUserId;
    this.nonprofitId = result.nonprofitId;

    if (this.nonprofitId) {
      this.activeWidget = "impact-overview";
    } else {
      this.activeWidget = "redeem-transaction";
    }

    this.localStorage.removeItem("transaction");
    this.localStorage.removeItem("cart");
    deleteCookieValue({ name: BEAM_CART_COOKIE_NAME, path: "/", domain: this.domain });

    // Emit order created event only when order is registered with Beam for the first time
    if (result.transactionCreated) {
      dispatchEvent(
        new BeamOrderCreatedEvent({
          transactionId: result.transactionId,
          orderId: this.orderId,
          cartTotal: this.cartTotal,
          currencyCode: this.currencyCode,
        })
      );
    }

    return result;
  };

  private transactionDataController = new AsyncController<typeof this.handlePostTransaction>(
    this,
    this.handlePostTransaction
  );

  render() {
    if (this.transactionDataController.loading) {
      return "";
    }
    if (this.transactionDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.transactionDataController.error });
      }
      return "";
    }
    if (this.activeWidget === "redeem-transaction") {
      return html`
        <beam-redeem-transaction
          part="redeem-transaction"
          baseUrl="${this.baseUrl}"
          apiKey="${this.apiKey}"
          storeId="${this.storeId}"
          transactionId="${this.transactionId}"
          cart="${JSON.stringify(this.cart)}"
          lang="${this.lang}"
          ?debug="${this.debug}"
          @beamnonprofitselect="${(event: Event) => {
            if (!(event instanceof CustomEvent)) return; // TS guard
            const { selectedNonprofitId } = (event as BeamNonprofitSelectEvent).detail;
            this.nonprofitId = selectedNonprofitId ?? null;
            this.activeWidget = "impact-overview";
          }}"
        ></beam-redeem-transaction>
      `;
    }
    if (this.activeWidget === "impact-overview") {
      return html`
        <beam-impact-overview
          part="impact-overview"
          baseUrl="${this.baseUrl}"
          apiKey="${this.apiKey}"
          chainId="${this.chainId}"
          userId="${this.userId}"
          nonprofitId="${this.nonprofitId}"
          lang="${this.lang}"
          ?debug="${this.debug}"
        ></beam-impact-overview>
      `;
    }
    return "";
  }

  static styles = [cssReset];
}

defineCustomElement(BeamPostPurchase);

declare global {
  interface HTMLElementTagNameMap {
    "beam-post-purchase": BeamPostPurchase;
  }
}
